import React from "react";
import Modal from "react-modal";
import { Field, Form, Formik } from "formik";
import { Oval } from "react-loader-spinner";
import * as Yup from "yup";
import toast from "react-hot-toast";

import { useAppSelector, useAppDispatch } from "../../../../../../app/hooks";
import { closeCreateModal, createApiKey } from "../../../../apiKeysSlice";
import { ApiKeyRole } from "../../../../../../models";

import "./CreateApiKeyModal.scss";

const modalStyles = {
  content: {
    top: "50%", left: "50%", right: "auto", bottom: "auto",
    marginRight: "-50%", transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
  },
};

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required("Name is required"),
  role: Yup.mixed<ApiKeyRole>()
    .oneOf([ApiKeyRole.Read, ApiKeyRole.Write])
    .required("Role is required"),
});

interface CreateApiKeyFormModel {
  name: string;
  role: ApiKeyRole;
}

const initialValues: CreateApiKeyFormModel = {
  name: "",
  role: ApiKeyRole.Read,
};

export function CreateApiKeyModal() {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.apiKeys.showCreateModal);
  const createAPI = useAppSelector((state) => state.apiKeys.createApiKey);

  React.useEffect(() => {
    if (!createAPI.loading && !!createAPI.error) {
      toast.error("Couldn't create the API key. Please try again.");
    }
  }, [createAPI.loading, createAPI.error]);

  const onClose = () => dispatch(closeCreateModal());

  const onSubmit = (form: CreateApiKeyFormModel) => {
    dispatch(createApiKey({ name: form.name.trim(), role: form.role }));
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyles}>
      <div className="create-api-key-modal-component">
        <div className="title">Create API Key</div>
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, touched }) => (
            <Form className="create-api-key-form">
              <div className="field-container">
                <div className="label">Name:</div>
                <Field id="name" name="name" className="text-input" />
                {errors.name && touched.name && (
                  <div className="form-error">{errors.name}</div>
                )}
              </div>

              <div className="field-container">
                <div className="label">Role:</div>
                <Field as="select" id="role" name="role" className="text-input">
                  <option value={ApiKeyRole.Read}>Read</option>
                  <option value={ApiKeyRole.Write}>Write</option>
                </Field>
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={createAPI.loading}
              >
                Create
                {createAPI.loading && (
                  <div style={{ marginLeft: 8 }}>
                    <Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A" />
                  </div>
                )}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
